<template>
  <section class="visited-section" aria-labelledby="visited-countries-heading">
    <h2 id="visited-countries-heading" class="section-title">
      <span class="globe" aria-hidden="true">🌍</span>
      Países visitados
    </h2>
    <p class="persist-hint">A lista é salva automaticamente na sua conta ao adicionar ou remover um país.</p>
    <p v-if="saveError" class="persist-error" role="alert">{{ saveError }}</p>
    <p class="label-upper">Adicionar novo país</p>
    <div class="search-wrap">
      <span class="search-icon" aria-hidden="true">🔍</span>
      <input
        ref="searchInput"
        v-model="query"
        type="search"
        class="search-input"
        placeholder="Pesquisar país para adicionar…"
        autocomplete="off"
        aria-autocomplete="list"
        :aria-activedescendant="activeDescendantId"
        :aria-expanded="dropdownOpen ? 'true' : 'false'"
        aria-controls="country-suggest-list"
        @focus="onFocus"
        @blur="scheduleClose"
        @keydown="onSearchKeydown"
      />
      <ul
        v-show="dropdownOpen && suggestions.length"
        id="country-suggest-list"
        class="suggest-list"
        role="listbox"
      >
        <li
          v-for="(c, i) in suggestions"
          :id="'country-opt-' + i"
          :key="c.code"
          :class="{ highlight: i === highlightIndex }"
          role="option"
          :aria-selected="i === highlightIndex ? 'true' : 'false'"
          @mousedown.prevent="addCountry(c.code)"
        >
          <span class="s-flag" aria-hidden="true">{{ flagEmoji(c.code) }}</span>
          {{ c.name }}
        </li>
      </ul>
    </div>
    <p class="label-upper">Minha jornada</p>
    <p v-if="!modelValue.length" class="empty-hint">Adicione os países que você já visitou</p>
    <div v-else class="pills" role="list">
      <span v-for="code in modelValue" :key="code" class="pill" role="listitem">
        <span class="p-flag" aria-hidden="true">{{ flagEmoji(code) }}</span>
        <span class="p-name">{{ countryName(code) }}</span>
        <button type="button" class="pill-x" :aria-label="'Remover ' + countryName(code)" @click="remove(code)">×</button>
      </span>
    </div>
  </section>
</template>

<script>
import { allCountriesPtSorted, countryNamePt, flagEmojiFromAlpha2, normalizeSearch } from "../utils/countryDisplay";

export default {
  name: "VisitedCountriesSection",
  props: {
    modelValue: { type: Array, default: () => [] },
    saveError: { type: String, default: "" },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      query: "",
      dropdownOpen: false,
      highlightIndex: -1,
      closeTimer: null,
      allCountries: allCountriesPtSorted(),
    };
  },
  watch: {
    query() {
      this.highlightIndex = this.suggestions.length ? 0 : -1;
      // Reabre a lista ao digitar (após addCountry o foco pode não disparar @focus de novo).
      if (this.query.trim() && this.suggestions.length > 0) {
        this.dropdownOpen = true;
      }
    },
  },
  computed: {
    suggestions() {
      const selected = new Set((this.modelValue || []).map((c) => String(c).toUpperCase()));
      const q = normalizeSearch(this.query.trim());
      if (!q) return [];
      const out = [];
      for (const row of this.allCountries) {
        if (selected.has(row.code)) continue;
        const nn = normalizeSearch(row.name);
        if (nn.includes(q) || row.code.toLowerCase().includes(q)) {
          out.push(row);
          if (out.length >= 12) break;
        }
      }
      return out;
    },
    activeDescendantId() {
      if (!this.dropdownOpen || this.highlightIndex < 0) return undefined;
      return "country-opt-" + this.highlightIndex;
    },
  },
  methods: {
    flagEmoji: flagEmojiFromAlpha2,
    countryName: countryNamePt,
    onFocus() {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
      this.dropdownOpen = true;
      this.highlightIndex = this.suggestions.length ? 0 : -1;
    },
    scheduleClose() {
      this.closeTimer = setTimeout(() => {
        this.dropdownOpen = false;
        this.highlightIndex = -1;
      }, 120);
    },
    onSearchKeydown(e) {
      if (!this.dropdownOpen && (e.key === "ArrowDown" || e.key === "ArrowUp") && this.suggestions.length) {
        this.dropdownOpen = true;
        this.highlightIndex = 0;
        e.preventDefault();
        return;
      }
      if (!this.suggestions.length) return;
      if (e.key === "Escape") {
        this.dropdownOpen = false;
        this.highlightIndex = -1;
        e.preventDefault();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        this.highlightIndex = Math.min(this.highlightIndex + 1, this.suggestions.length - 1);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        this.highlightIndex = Math.max(this.highlightIndex - 1, 0);
      } else if (e.key === "Enter" && this.highlightIndex >= 0) {
        e.preventDefault();
        const c = this.suggestions[this.highlightIndex];
        if (c) this.addCountry(c.code);
      }
    },
    addCountry(code) {
      const u = String(code).toUpperCase();
      if ((this.modelValue || []).some((x) => String(x).toUpperCase() === u)) return;
      this.$emit("update:modelValue", [...(this.modelValue || []), u]);
      this.query = "";
      this.highlightIndex = -1;
      if (this.closeTimer) clearTimeout(this.closeTimer);
      this.$nextTick(() => {
        const el = this.$refs.searchInput;
        el?.focus();
        // Não usar dropdownOpen = false aqui: com mousedown na lista o input às vezes
        // continua focado e focus() não dispara onFocus — a lista ficaria permanentemente fechada.
        this.dropdownOpen = true;
      });
    },
    remove(code) {
      const u = String(code).toUpperCase();
      this.$emit(
        "update:modelValue",
        (this.modelValue || []).filter((x) => String(x).toUpperCase() !== u),
      );
    },
  },
};
</script>

<style scoped>
.visited-section {
  background: #152338;
  border: 1px solid #1e3352;
  border-radius: 14px;
  padding: 22px 22px 20px;
  margin-bottom: 20px;
  box-shadow: 0 12px 32px -4px rgba(0, 0, 0, 0.3);
}
.persist-hint {
  margin: -10px 0 16px;
  font-size: 13px;
  color: #94adc8;
  line-height: 1.4;
}
.persist-error {
  margin: -8px 0 14px;
  font-size: 13px;
  font-weight: 600;
  color: #f87171;
}
.section-title {
  margin: 0 0 8px;
  font-size: 1.25rem;
  font-weight: 700;
  color: #e2eaf4;
  letter-spacing: -0.02em;
  display: flex;
  align-items: center;
  gap: 8px;
}
.globe {
  font-size: 1.2rem;
}
.label-upper {
  margin: 0 0 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #94adc8;
}
.search-wrap {
  position: relative;
  margin-bottom: 20px;
}
.search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0.55;
  font-size: 14px;
  pointer-events: none;
}
.search-input {
  width: 100%;
  box-sizing: border-box;
  padding: 14px 14px 14px 40px;
  font-size: 15px;
  border: 1px solid #1e3352;
  border-radius: 12px;
  background: #081526;
  color: #e2eaf4;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.search-input:focus {
  border-color: #4f8ef7;
  box-shadow: 0 0 0 2px rgba(79, 142, 247, 0.15);
}
.search-input::placeholder {
  color: #4a6080;
}
.suggest-list {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 6px);
  margin: 0;
  padding: 8px 0;
  list-style: none;
  background: #152338;
  border: 1px solid #1e3352;
  border-radius: 12px;
  box-shadow: 0 12px 32px -4px rgba(0, 0, 0, 0.4);
  max-height: 260px;
  overflow-y: auto;
  z-index: 20;
}
.suggest-list li {
  padding: 10px 14px;
  cursor: pointer;
  font-size: 14px;
  color: #e2eaf4;
  display: flex;
  align-items: center;
  gap: 8px;
}
.suggest-list li.highlight {
  background: #1e3a8a;
  color: #93c5fd;
}
.s-flag {
  font-size: 1.1rem;
  line-height: 1;
}
.empty-hint {
  margin: 4px 0 0;
  font-size: 14px;
  color: #94adc8;
}
.pills {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 4px;
}
.pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px 8px 12px;
  border-radius: 999px;
  background: #1a2e48;
  border: 1px solid #1e3352;
  color: #94adc8;
  font-size: 14px;
  font-weight: 600;
  transition: border-color 0.15s ease;
}
.pill:hover {
  border-color: #4f8ef7;
}
.p-flag {
  font-size: 1.1rem;
  line-height: 1;
}
.p-name {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pill-x {
  border: none;
  background: transparent;
  color: #4a6080;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  padding: 0 2px;
  margin-left: 2px;
  border-radius: 4px;
}
.pill-x:hover {
  color: #e2eaf4;
  background: rgba(255, 255, 255, 0.08);
}
</style>
